import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Pessoa } from './pessoa';
import { PessoaDto } from './pessoaDto';
import { PessoaFotoUploadRequest, PessoaFotoUploadResponse } from './pessoa-foto-upload';
import { TurmaCatequizandoDto } from './turma-catequizando.dto';
import { PessoaTheosDetalheDto } from './pessoa-theos-detalhe.dto';
import { PessoaTheosComparacaoDto } from './pessoa-theos-comparacao.dto';
import { Pagina } from './pagina';


@Injectable({
  providedIn: 'root'
})
export class PessoaService {

  private apiUrl = `${environment.api}/pessoas`;

  constructor(private http: HttpClient) { }

  /**
   * `GET /pessoas` → `PagePessoaDto`; com filtro, `GET /pessoas/search`, que o exige.
   *
   * `TAB-01` — a ordenação é do servidor: ordenar só a página carregada mente, porque o
   * "maior primeiro" seria o maior **da página**. `CTR-12` — `sort` ausente não vira
   * parâmetro; o contrato já ordena por nome ascendente nesse caso.
   */
  listar(page: number, size: number, filtro: string, sort?: string | null): Observable<Pagina<Pessoa>> {
    let params = new HttpParams().set('page', String(page)).set('size', String(size));
    if (sort) params = params.set('sort', sort);

    if (filtro && filtro.trim() !== '') {
      return this.http.get<Pagina<Pessoa>>(`${this.apiUrl}/search`, {
        params: params.set('filtro', filtro),
      });
    }
    return this.http.get<Pagina<Pessoa>>(`${this.apiUrl}`, { params });
  }

  /** Filtros combinados (AND): nome, cpf, telefone — cada um opcional. */
  buscaComFiltros(
    page: number,
    size: number,
    nome?: string,
    cpf?: string,
    telefone?: string,
    sort?: string | null,
  ): Observable<Pagina<Pessoa>> {
    let params = new HttpParams().set('page', String(page)).set('size', String(size));
    if (nome?.trim()) params = params.set('nome', nome.trim());
    if (cpf?.trim()) params = params.set('cpf', cpf.trim());
    if (telefone?.trim()) params = params.set('telefone', telefone.trim());
    if (sort) params = params.set('sort', sort);
    return this.http.get<Pagina<Pessoa>>(`${this.apiUrl}/busca`, { params });
  }


  listarSemTurma(ano: number): Observable<Pessoa[]> {
    return this.http.get<Pessoa[]>(`${this.apiUrl}/sem-turma`, {
      params: { ano: ano.toString() }
    });
  }

  buscarPorId(id: string): Observable<Pessoa> {
    return this.http.get<Pessoa>(`${this.apiUrl}/${id}`);
  }

  /** GET /pessoas/{id}/turmas — matrículas como catequizando */
  listarTurmasPessoa(pessoaId: string): Observable<TurmaCatequizandoDto[]> {
    return this.http.get<TurmaCatequizandoDto[]>(
      `${this.apiUrl}/${encodeURIComponent(pessoaId)}/turmas`,
    );
  }

  /** GET /pessoas/{id}/theos — resumo da integração Theos */
  buscarDetalhesTheos(pessoaId: string): Observable<PessoaTheosDetalheDto> {
    return this.http.get<PessoaTheosDetalheDto>(
      `${this.apiUrl}/${encodeURIComponent(pessoaId)}/theos`,
    );
  }

  /** GET /pessoas/{id}/theos/comparacao — campos do Theos para comparar com o formulário */
  buscarComparacaoTheos(pessoaId: string): Observable<PessoaTheosComparacaoDto> {
    return this.http.get<PessoaTheosComparacaoDto>(
      `${this.apiUrl}/${encodeURIComponent(pessoaId)}/theos/comparacao`,
    );
  }

  /** POST /pessoas/{id}/theos/enviar — envia dados locais ao Theos (PUT) */
  enviarDadosParaTheos(pessoaId: string): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/${encodeURIComponent(pessoaId)}/theos/enviar`,
      {},
    );
  }

  buscarPorIds(ids: string[]): Observable<Pessoa[]> {
    return this.http.post<Pessoa[]>(`${this.apiUrl}/by-ids`, ids);
  }

  salvar(dto: PessoaDto): Observable<Pessoa> {
    return this.http.post<Pessoa>(this.apiUrl, dto);
  }

  atualizar(id: string, dto: PessoaDto): Observable<Pessoa> {
    return this.http.put<Pessoa>(`${this.apiUrl}/${id}`, dto);
  }

  /**
   * POST /pessoas/{id}/foto — id = pessoaId.
   */
  uploadFoto(pessoaId: string, body: PessoaFotoUploadRequest): Observable<PessoaFotoUploadResponse> {
    return this.http.post<PessoaFotoUploadResponse>(`${this.apiUrl}/${encodeURIComponent(pessoaId)}/foto`, body);
  }

  /**
   * GET /pessoas/{pessoaId}/foto — URL direta (ex.: img src ou nova aba).
   */
  urlFotoPessoa(pessoaId: string): string {
    return `${this.apiUrl}/${encodeURIComponent(pessoaId)}/foto`;
  }

  /** DELETE /pessoas/{id}/foto — remove vínculo da foto na pessoa. */
  removerFoto(pessoaId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${encodeURIComponent(pessoaId)}/foto`);
  }

}
